/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Phase 6e2c-3 — staging-backed adapters for the selector's Mounting tab.
 *
 * The legacy selector builds a FAKE formView from the STAGING models and hands
 * it to MountingViewPopup (withPopup:false → renderInContainer); critically the
 * fake createPosition/render NEVER persist — they only set the staging position
 * model (accessory.selector.js:169-197). The real AccessoryPositionModel.set
 * auto-PATCHes when armed, so it must NOT back the Mounting tab.
 *
 * These adapters give the REUSED MountingModel (pure, no URL) + the REUSED
 * AccessoryMountingPopup (inlineContainer mode) exactly the surface they read,
 * but every write lands on plain staging objects — zero network. The selector's
 * own getAccessoryData round-trip (userArmed-gated) is the ONLY write path.
 */

import { MountingModel } from '../accessory/mountingModel';
import type { MountingSectionDims } from '../accessory/mountingMath';
import type { StagingModel } from './accessorySelectorStaging';

/** Plain subscribable wrapper over the staging accessory model (FormModel-ish
 * surface MountingModel + the popup read: get / markUserAction / set / toJSON). */
export class StagingFormAdapter {
  private subs = new Set<() => void>();

  constructor(private staging: StagingModel) {}

  get<T = unknown>(key: string): T {
    return this.staging.get<T>(key);
  }

  set(attrs: Record<string, unknown>): void {
    this.staging.set(attrs);
    this.bump();
  }

  toJSON(): Record<string, unknown> {
    return this.staging.toJSON();
  }

  markUserAction(): void {
    /* staging never persists — no-op (the fake formView likewise) */
  }

  subscribe(cb: () => void): () => void {
    this.subs.add(cb);
    return () => this.subs.delete(cb);
  }

  bump(): void {
    this.subs.forEach((cb) => cb());
  }
}

/** Plain subscribable position (no PATCH) — mirrors the legacy staging
 * position model. Surface: get / set / subscribe / toJSON / markUserAction +
 * `mountingType` (the popup's on_internal_wall early-return reads it). */
export class StagingPositionAdapter {
  attributes: Record<string, unknown>;

  mountingType: 'on_cabinet' | 'on_internal_wall';

  private subs = new Set<() => void>();

  constructor(initial: Record<string, unknown> = {}, mountingType?: string | null) {
    this.attributes = { ...initial };
    this.mountingType = mountingType === 'on_internal_wall' ? 'on_internal_wall' : 'on_cabinet';
  }

  get<T = unknown>(key: string): T {
    return this.attributes[key] as T;
  }

  set(attrs: Record<string, unknown>): void {
    let changed = false;
    Object.keys(attrs).forEach((k) => {
      if (this.attributes[k] !== attrs[k]) changed = true;
      this.attributes[k] = attrs[k];
    });
    if (changed) this.bump();
  }

  toJSON(): Record<string, unknown> {
    return { ...this.attributes };
  }

  markUserAction(): void {
    /* staging never persists */
  }

  subscribe(cb: () => void): () => void {
    this.subs.add(cb);
    return () => this.subs.delete(cb);
  }

  private bump(): void {
    this.subs.forEach((cb) => cb());
  }
}

/** Build a MountingModel over the staging adapters (REUSE — pure math, no URL). */
export function buildStagingMounting(
  form: StagingFormAdapter,
  position: StagingPositionAdapter,
  getSectionDims: () => MountingSectionDims,
  getMountingPlanes: () => any[]
): MountingModel {
  return new MountingModel({
    // the adapters satisfy the FormModel/PositionModel surface MountingModel reads.
    accessory: form as any,
    position: position as any,
    getSectionDims,
    getMountingPlanes,
  });
}
